import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { formatDateForLocale } from '@/lib/dateUtils'
import { cn } from '@/lib/utils'
import type { RecoverRequest } from '../api'
import { useWorkforce } from './api'
import { useCandidates } from './useCandidates'
import { CandidateCard } from './CandidateCard'

/**
 * Assign an engineer, with the trade-off stated.
 *
 * Replaces a flat alphabetical table of every user — which asked command and control to hold site,
 * team, availability and load in their head and pick. This ranks, gives each candidate one honest
 * reason for and one against, and still makes a human choose: NOTHING IS PRESELECTED, and the
 * primary button stays disabled until somebody picks.
 */
export function RankedAssignDialog({
  open, onOpenChange, row, locale, onConfirm, isLoading = false,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  row: RecoverRequest | null
  locale: string
  onConfirm: (userId: number) => void
  isLoading?: boolean
}) {
  const { t } = useTranslation()
  const [picked, setPicked] = useState<number | null>(null)
  const workforce = useWorkforce(open)
  const { candidates, access, eligibleCount, poolCount } = useCandidates(row, workforce.data)

  // A selection left over from the previous object is a selection against the wrong object.
  useEffect(() => { setPicked(null) }, [row?.id, open])

  const when = useMemo(() => {
    if (!row?.forecastStartTime) return '—'
    try {
      return formatDateForLocale(
        typeof row.forecastStartTime === 'string' ? row.forecastStartTime : row.forecastStartTime.toISOString(),
        locale, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false })
    } catch { return '—' }
  }, [row, locale])

  const isGo = String(row?.waitGo ?? '').toUpperCase() === 'GO'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[86vh] flex-col sm:max-w-2xl">
        <DialogHeader className="shrink-0">
          <DialogTitle>{t('assignment.assignTitle', 'Assign engineer')}</DialogTitle>
          <DialogDescription asChild>
            <div className="mt-2 rounded-lg border bg-muted/30 p-3">
              <div className="text-sm font-semibold text-foreground">{row?.objectID}</div>
              <div className="mt-0.5 text-xs text-muted-foreground">{row?.objectDescription || '—'}</div>
              <div className="mt-2.5 grid grid-cols-2 gap-x-5 gap-y-1.5 text-xs sm:grid-cols-4">
                <Field label={t('recoverRequests.location', 'Location')} value={row?.location ?? '—'} />
                <Field label={t('recoverRequests.team', 'Team')} value={row?.teamName || '—'} muted={!row?.teamName} />
                <Field label={t('recoverRequests.forecastStartTime', 'Forecast start')} value={when} />
                <Field
                  label={t('assignment.statusLabel', 'Status')}
                  value={`${isGo ? 'Go' : 'Wait'}${row?.objectReady ? ' · Ready' : ''}`}
                />
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto pt-1">
          <p className="mb-2 text-[11.5px] leading-snug text-muted-foreground">
            {t('assignment.rankingExplained',
              'Ranked by site match, then primary team, then availability, then current load. The ranking is advisory — nothing is preselected.')}
          </p>

          {access === 'on-site' && (
            <p className="mb-2 rounded-lg px-3 py-2 text-[11.5px] leading-snug"
               style={{ backgroundColor: 'rgba(245,158,11,0.09)', color: 'rgb(146,88,8)' }}>
              {t('assignment.onSiteAdvisory',
                'This object needs physical access, so only {{eligible}} of {{pool}} engineers are on site. The others can still be assigned — each card says how far away they are.',
                { eligible: eligibleCount, pool: poolCount })}
            </p>
          )}

          <div className="flex flex-col gap-1.5">
            {workforce.isLoading && (
              <div className="py-8 text-center text-xs text-muted-foreground">{t('common.loading', 'Loading…')}</div>
            )}
            {!workforce.isLoading && candidates.length === 0 && (
              <div className="py-8 text-center text-xs text-muted-foreground">
                {t('assignment.noEngineers', 'No engineers are recorded against any site yet.')}
              </div>
            )}
            {candidates.map((c, i) => (
              <CandidateCard
                key={c.engineer.id}
                candidate={c}
                rank={i + 1}
                showRadio
                selected={picked === c.engineer.id}
                // Selectable whether or not they qualify. Eligibility is ADVICE — the card
                // already states the reason and the distance — and command and control
                // routinely has to send someone who does not qualify on paper: the nearest
                // engineer is four hours away, or the only person who knows the object is on
                // the wrong team. Blocking the choice does not make that situation better, it
                // just moves the decision off the screen that has the facts.
                onSelect={() => setPicked(c.engineer.id)}
              />
            ))}
          </div>

          <p className="mt-3 rounded-lg border px-3 py-2 text-[11px] leading-relaxed"
             style={{ backgroundColor: 'rgba(245,158,11,0.07)', borderColor: 'rgba(245,158,11,0.35)', color: 'rgb(146,88,8)' }}>
            {t('assignment.shiftUnknown',
              '“Finishes before handover?” cannot be shown — engineer shift start and end is a proposed new field, not in the database today.')}
          </p>
        </div>

        <DialogFooter className="shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            // Only "has somebody been picked". An empty eligible pool is a fact about the
            // estate, not a reason the dialog cannot do its job.
            disabled={picked == null || isLoading}
            onClick={() => { if (picked != null) onConfirm(picked) }}
          >
            {t('assignment.assignEngineer', 'Assign Engineer')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function Field({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={cn('mt-0.5 truncate text-xs font-medium', muted && 'text-muted-foreground')}>{value}</div>
    </div>
  )
}
